import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CalendarDays,
  ChevronDown,
  Clock3,
  Columns3,
  LayoutDashboard,
  Package,
  Puzzle,
  RefreshCcw,
  Smartphone,
  UserCheck,
  UserPlus,
  UserX,
  Users,
  Waypoints,
} from 'lucide-react';

import { Button } from '@/ui/button';
import { Card, CardContent } from '@/ui/card';
import { Input } from '@/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/ui/select';
import { cn } from '@/lib/utils';
import { formatDateStandard } from 'smart-react';

import { useAuth } from '../../Core/Context/AuthContext';
import { listOrganizationUser } from '../../Core/OrganizationUsers/Services/OrganizationUserService';
import { getOrganizationDetail } from '../../Organization/OrganizationDetail/Services/OrganizationService';
import { listProducts } from '../../Products/Services/ProductService';
import {
  DASHBOARD_TILELAYOUT,
  DATE_RANGE_OPTIONS,
  DEFAULT_TENANT_NAME,
  REFRESH_INTERVAL,
} from '../../../constants/applicationConstants';
import {
  getDefaultTenant,
  getSubscription,
  hasFeaturePermissions,
  isAdminRole,
  isAllProductFeaturesExpired,
  isAllProductFeaturesSubscribed,
  isAnyProductFeatureExpired,
  isFeatureExpired,
  isOrganizationAdminRole,
} from '../../../Utils/Auth/ValidatePermissions';

const DEFAULT_STATS = {
  totalMembers: 0,
  activeMembers: 0,
  inactiveMembers: 0,
  newMembersThisMonth: 0,
};

const PRODUCT_TONES = [
  { bg: 'bg-primary/12', text: 'text-primary' },
  { bg: 'bg-sky-500/12', text: 'text-sky-600 dark:text-sky-400' },
  { bg: 'bg-emerald-500/12', text: 'text-emerald-600 dark:text-emerald-400' },
  { bg: 'bg-amber-500/12', text: 'text-amber-600 dark:text-amber-400' },
  { bg: 'bg-rose-500/12', text: 'text-rose-600 dark:text-rose-400' },
];

const AVATAR_TONES = [
  'bg-primary/12 text-primary',
  'bg-emerald-500/12 text-emerald-600 dark:text-emerald-400',
  'bg-orange-500/12 text-orange-600 dark:text-orange-400',
  'bg-sky-500/12 text-sky-600 dark:text-sky-400',
];

const STATUS_BADGE_STYLES = {
  subscribed:
    'border-emerald-500/15 bg-emerald-500/8 text-emerald-700 dark:text-emerald-300',
  expired:
    'border-rose-500/15 bg-rose-500/8 text-rose-700 dark:text-rose-300',
  partial:
    'border-amber-500/15 bg-amber-500/8 text-amber-700 dark:text-amber-300',
};

const DASHBOARD_FONT_FAMILY =
  'Geist, "Geist Fallback", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Arial, "Noto Sans", "Liberation Sans", sans-serif';
const DESIGN_GUIDE = {
  cardPadding: 'p-5',
  labelClass:
    'text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground',
  bodyTextClass: 'text-[13px] leading-6 text-muted-foreground',
  metricValueClass:
    'text-[28px] font-semibold leading-none tracking-[-0.03em] text-foreground',
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const safeParse = (value, fallback) => {
  if (!value) {
    return fallback;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    return fallback;
  }
};

const formatDateToInputValue = (date) => date.toISOString().split('T')[0];

const formatDateToDisplayValue = (value) => {
  if (!value) {
    return 'Select date';
  }

  const parsedDate = new Date(`${value}T00:00:00`);

  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  }).format(parsedDate);
};

const getDefaultDateRange = () => {
  const today = new Date();
  const lastWeek = new Date(today);
  lastWeek.setDate(today.getDate() - 7);

  return {
    startDate: formatDateToInputValue(lastWeek),
    endDate: formatDateToInputValue(today),
  };
};

const getStoredDateFilter = () => {
  if (typeof window === 'undefined') {
    return {
      range: '7',
      dates: getDefaultDateRange(),
    };
  }

  const storedValue = safeParse(
    window.localStorage.getItem(`${DASHBOARD_TILELAYOUT}_DATE_FILTER`),
    null,
  );

  return {
    range: storedValue?.range ?? '7',
    dates: storedValue?.dates ?? getDefaultDateRange(),
  };
};

const getStoredRefreshInterval = () => {
  if (typeof window === 'undefined') {
    return REFRESH_INTERVAL[1];
  }

  const storedValue = safeParse(
    window.localStorage.getItem(`${DASHBOARD_TILELAYOUT}_REFRESH_INTERVAL`),
    null,
  );

  if (!storedValue) {
    return REFRESH_INTERVAL[1];
  }

  return (
    REFRESH_INTERVAL.find((option) => option.value === storedValue?.value) ||
    REFRESH_INTERVAL.find((option) => option.label === storedValue?.label) ||
    REFRESH_INTERVAL.find((option) => option.label === storedValue?.text) ||
    REFRESH_INTERVAL[1]
  );
};

const getGreeting = () => {
  const currentHour = new Date().getHours();

  if (currentHour < 12) return 'Good morning';
  if (currentHour < 18) return 'Good afternoon';
  return 'Good evening';
};

const extractName = (value) => {
  if (!value || typeof value !== 'string') {
    return 'User';
  }

  const normalizedValue = value.includes('@')
    ? value.split('@')[0]
    : value.replace(/\./g, ' ');
  const words = normalizedValue
    .split(/[\s._-]+/)
    .filter(Boolean)
    .map((item) => item.charAt(0).toUpperCase() + item.slice(1));

  return words.length > 0 ? words.join(' ') : 'User';
};

const getInitials = (firstName, lastName, fallbackValue) => {
  const fullName = `${firstName ?? ''} ${lastName ?? ''}`.trim() || fallbackValue;
  const parts = fullName.split(/\s+/).filter(Boolean);

  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }

  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }

  return 'U';
};

const formatShortDate = (value) => {
  if (!value) {
    return '--';
  }

  return new Date(value).toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
  });
};

const getStatusBadge = (status) => {
  if (status === 'Expired') {
    return {
      label: 'Expired',
      className: STATUS_BADGE_STYLES.expired,
    };
  }

  if (status === 'Partially active') {
    return {
      label: 'Partially active',
      className: STATUS_BADGE_STYLES.partial,
    };
  }

  return {
    label: 'Subscribed',
    className: STATUS_BADGE_STYLES.subscribed,
  };
};

const getProductStatus = (product) => {
  const features = Array.isArray(product?.features) ? product.features : [];

  if (isAllProductFeaturesExpired(features)) {
    return getStatusBadge('Expired');
  }

  if (
    isAllProductFeaturesSubscribed(product) &&
    !isAnyProductFeatureExpired(features)
  ) {
    return getStatusBadge('Subscribed');
  }

  return getStatusBadge('Partially active');
};

const getSubscribedFeatureRows = (product) =>
  (Array.isArray(product?.features) ? product.features : [])
    .filter((feature) => Array.isArray(feature?.Permissions) && feature.Permissions.length > 0)
    .map((feature) => {
      const subscription = getSubscription(feature?.Permissions);
      const isExpired =
        !subscription?.Active || isFeatureExpired(subscription?.SubscriptionEndDate);

      return {
        id:
          feature?.feature_id ||
          feature?.FeatureId ||
          feature?.name ||
          subscription?.FeatureId,
        name: feature?.display_name || feature?.name || 'Untitled feature',
        expirationDate: subscription?.SubscriptionEndDate,
        status: getStatusBadge(isExpired ? 'Expired' : 'Subscribed'),
      };
    });

const getProductKey = (product, index) =>
  product?.product_id || product?.ProductId || product?.display_name || `product-${index}`;

const getProductVisual = (product, index) => {
  const name = (product?.display_name ?? '').toLowerCase();
  const tone = PRODUCT_TONES[index % PRODUCT_TONES.length];

  if (name.includes('dashboard')) {
    return {
      Icon: LayoutDashboard,
      subtitle: product?.description || 'Overview & analytics',
      tone,
    };
  }

  if (name.includes('board')) {
    return {
      Icon: Columns3,
      subtitle: product?.description || 'Task management',
      tone,
    };
  }

  if (name.includes('oogy') || name.includes('integration')) {
    return {
      Icon: Puzzle,
      subtitle: product?.description || 'Integrations',
      tone,
    };
  }

  if (name.includes('moca') || name.includes('mobile')) {
    return {
      Icon: Smartphone,
      subtitle: product?.description || 'Mobile client',
      tone,
    };
  }

  if (name.includes('rf')) {
    return {
      Icon: Waypoints,
      subtitle: product?.description || 'RF management',
      tone,
    };
  }

  return {
    Icon: Package,
    subtitle:
      product?.description ||
      `${product?.features?.length ?? 0} feature${product?.features?.length === 1 ? '' : 's'} enabled`,
    tone,
  };
};

const DashboardSurface = ({ className, ...props }) => (
  <Card
    className={cn('rounded-lg border border-border/40 bg-card shadow-none', className)}
    {...props}
  />
);

const WorkspaceMetricCardSkeleton = () => (
  <DashboardSurface className='overflow-hidden rounded-lg border-border/40 bg-card'>
    <CardContent className={DESIGN_GUIDE.cardPadding}>
      <div className='flex items-start justify-between gap-4'>
        <div className='space-y-3'>
          <div className='h-3 w-24 animate-pulse rounded bg-muted' />
          <div className='h-8 w-20 animate-pulse rounded bg-muted' />
        </div>
        <div className='h-11 w-11 animate-pulse rounded-lg bg-muted' />
      </div>
      <div className='mt-4 h-3 w-40 animate-pulse rounded bg-muted' />
      <div className='mt-5 h-1.5 animate-pulse rounded-full bg-muted' />
    </CardContent>
  </DashboardSurface>
);

const WorkspaceMetricCard = ({
  icon: Icon,
  label,
  value,
  helper,
  accentClassName,
  progressClassName,
  progressWidth,
}) => (
  <DashboardSurface className='relative h-full overflow-hidden rounded-lg border-border/40 bg-card'>
    <div className='absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-foreground/15 to-transparent opacity-70 dark:via-white/40' />
    <CardContent className={DESIGN_GUIDE.cardPadding}>
      <div className='flex items-start justify-between gap-4'>
        <div className='space-y-3'>
          <p className={DESIGN_GUIDE.labelClass}>{label}</p>
          <div className={DESIGN_GUIDE.metricValueClass}>{value}</div>
        </div>
        <span
          className={cn(
            'inline-flex h-11 w-11 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 shadow-sm dark:border-border/45 dark:bg-background/55 dark:shadow-none',
            accentClassName,
          )}
        >
          <Icon className='h-5 w-5' />
        </span>
      </div>
      <p className={cn('mt-3', DESIGN_GUIDE.bodyTextClass)}>{helper}</p>
      <div className='mt-5 h-1.5 overflow-hidden rounded-full bg-border/35'>
        <div
          className={cn(
            'h-full rounded-full bg-gradient-to-r transition-[width] duration-700',
            progressClassName,
          )}
          style={{ width: progressWidth }}
        />
      </div>
    </CardContent>
  </DashboardSurface>
);

const StatusBadge = ({ status }) => (
  <span
    className={cn(
      'inline-flex shrink-0 items-center rounded-md border px-1.5 py-0.5 text-[10px] font-medium capitalize tracking-normal',
      status.className,
    )}
  >
    {status.label.toLowerCase()}
  </span>
);

const HeroMetaPill = ({ icon: Icon, label }) => (
  <div className='inline-flex items-center gap-2 rounded-full border border-border/60 bg-background/80 px-3 py-1 text-[12px] font-medium text-muted-foreground'>
    <Icon className='h-3.5 w-3.5 shrink-0 text-foreground/70' />
    <span className='truncate'>{label}</span>
  </div>
);

const PanelHeader = ({ eyebrow, title, description, action }) => (
  <div className='flex flex-row items-start justify-between gap-3 border-b border-border/35 px-5 py-3.5'>
    <div className='space-y-0.5'>
      {eyebrow ? (
        <div className='text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground'>
          {eyebrow}
        </div>
      ) : null}
      <div className='text-[14px] font-semibold text-foreground'>{title}</div>
      {description ? (
        <div className='text-[13px] leading-4 text-muted-foreground'>{description}</div>
      ) : null}
    </div>
    {action}
  </div>
);

const DashboardDateField = ({ label, value, onChange }) => (
  <label className='flex min-w-[180px] flex-col gap-2'>
    <span className='text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground'>
      {label}
    </span>
    <div className='relative'>
      <div className='flex h-10 items-center gap-3 rounded-xl border border-border/60 bg-background/80 px-4 text-foreground shadow-none'>
        <CalendarDays className='h-4 w-4 shrink-0 text-muted-foreground' />
        <span className='text-[14px] font-medium'>
          {formatDateToDisplayValue(value)}
        </span>
      </div>
      <Input
        type='date'
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className='absolute inset-0 h-10 w-full cursor-pointer border-0 bg-transparent opacity-0 shadow-none [color-scheme:light] dark:[color-scheme:dark]'
      />
    </div>
  </label>
);

const DonutSummary = ({ percentage, activeMembers, inactiveMembers }) => {
  const radius = 34;
  const circumference = 2 * Math.PI * radius;
  const progress = Math.max(0, Math.min(percentage, 100));
  const dashLength = (progress / 100) * circumference;

  return (
    <div className='flex flex-col gap-4 sm:flex-row sm:items-center'>
      <div className='relative mx-auto h-[88px] w-[88px] shrink-0 sm:mx-0'>
        <svg className='h-[88px] w-[88px] -rotate-90' viewBox='0 0 96 96'>
          <circle
            cx='48'
            cy='48'
            r={radius}
            fill='none'
            stroke='oklch(var(--border) / 0.6)'
            strokeWidth='12'
          />
          <circle
            cx='48'
            cy='48'
            r={radius}
            fill='none'
            stroke='oklch(var(--primary))'
            strokeWidth='12'
            strokeLinecap='round'
            strokeDasharray={`${dashLength} ${circumference - dashLength}`}
          />
        </svg>
        <div className='absolute inset-0 flex items-center justify-center text-[14px] font-semibold text-foreground'>
          {progress}%
        </div>
      </div>

      <div className='flex-1 space-y-2.5'>
        <div className='flex items-center gap-2 text-[13px] text-muted-foreground'>
          <span className='h-2.5 w-2.5 rounded-full bg-primary' />
          <span>Active members</span>
          <span className='ml-auto text-[13px] font-semibold text-foreground'>
            {activeMembers}
          </span>
        </div>
        <div className='flex items-center gap-2 text-[13px] text-muted-foreground'>
          <span className='h-2.5 w-2.5 rounded-full bg-border' />
          <span>Inactive</span>
          <span className='ml-auto text-[13px] font-semibold text-foreground'>
            {inactiveMembers}
          </span>
        </div>
      </div>
    </div>
  );
};

const fetchOrganizationMembers = async ({ accessToken, tenantID, user }) => {
  const organizationResponse = await getOrganizationDetail({
    accessToken,
    isServiceWorker: false,
    user,
  });

  const organizations =
    organizationResponse?.data?.filter(
      (organization) => organization?.name !== DEFAULT_TENANT_NAME,
    ) ?? [];
  const defaultTenantId = getDefaultTenant()?.TenantId;
  const selectedOrganization =
    organizations.find((organization) => organization?.tenant_id === tenantID) ||
    organizations.find(
      (organization) => organization?.tenant_id === defaultTenantId,
    );
  const orgId = selectedOrganization?.organization_id;

  if (!orgId) {
    return {
      members: [],
      stats: DEFAULT_STATS,
    };
  }

  const response = await listOrganizationUser({
    limit: 500,
    offset: 0,
    filter: null,
    sort: null,
    moduleName: 'UserModule',
    isServiceWorker: false,
    listController: 'UserListController',
    orgId,
  });

  const members = Array.isArray(response?.organizationUsers)
    ? [...response.organizationUsers].sort(
        (memberA, memberB) =>
          new Date(memberB?.CreatedDate) - new Date(memberA?.CreatedDate),
      )
    : [];
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

  return {
    members,
    stats: {
      totalMembers:
        typeof response?.TotalCount === 'number' && response.TotalCount >= 0
          ? response.TotalCount
          : members.length,
      activeMembers: members.filter((member) => member?.Active).length,
      inactiveMembers: members.filter((member) => !member?.Active).length,
      newMembersThisMonth: members.filter(
        (member) => new Date(member?.CreatedDate) >= oneMonthAgo,
      ).length,
    },
  };
};

const Home = () => {
  const navigate = useNavigate();
  const { getTokensFromStorage, profileData, tenantID, user } = useAuth();

  const storedDateFilter = React.useMemo(() => getStoredDateFilter(), []);
  const storedInterval = React.useMemo(() => getStoredRefreshInterval(), []);
  const canShowOrganizationInsights = React.useMemo(
    () => isOrganizationAdminRole(user) || isAdminRole(user),
    [user],
  );

  const [selectedRange, setSelectedRange] = React.useState(
    storedDateFilter.range,
  );
  const [dates, setDates] = React.useState(storedDateFilter.dates);
  const [selectedInterval, setSelectedInterval] = React.useState(storedInterval);
  const [products, setProducts] = React.useState([]);
  const [members, setMembers] = React.useState([]);
  const [stats, setStats] = React.useState(DEFAULT_STATS);
  const [expandedProductId, setExpandedProductId] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [refreshTick, setRefreshTick] = React.useState(0);

  const firstName = profileData?.Appuservm?.FirstName;
  const lastName = profileData?.Appuservm?.LastName;
  const displayName = React.useMemo(() => {
    const formattedName = `${firstName ?? ''} ${lastName ?? ''}`.trim();

    if (formattedName) {
      return formattedName;
    }

    return extractName(
      user?.name || user?.userName || user?.username || user?.email,
    );
  }, [firstName, lastName, user]);

  const selectedRangeLabel =
    DATE_RANGE_OPTIONS.find((option) => option.value === selectedRange)?.text ||
    'Last 7 Days';
  const activePercentage = stats.totalMembers
    ? Math.round((stats.activeMembers / stats.totalMembers) * 100)
    : 0;
  const subscribedProducts = React.useMemo(
    () => products.filter((product) => hasFeaturePermissions(product?.features)),
    [products],
  );
  const visibleProducts = React.useMemo(
    () => subscribedProducts.slice(0, 5),
    [subscribedProducts],
  );
  const recentMembers = React.useMemo(
    () =>
      [...members]
        .sort((firstMember, secondMember) => {
          const firstDate = new Date(firstMember?.CreatedDate ?? 0).getTime();
          const secondDate = new Date(secondMember?.CreatedDate ?? 0).getTime();

          return secondDate - firstDate;
        })
        .slice(0, 3),
    [members],
  );
  const refreshCadenceLabel =
    selectedInterval?.value === 'off'
      ? 'Manual refresh only'
      : selectedInterval?.text ||
        selectedInterval?.label ||
        'Auto refresh enabled';
  const selectedRangeControlLabel =
    selectedRange === 'custom' ? 'Custom Range' : selectedRangeLabel;
  const selectedRangeMetricValue =
    selectedRange === 'custom'
      ? 'Custom'
      : selectedRangeLabel.replace(/^Last\s+/i, '');
  const workspaceOverviewCards = React.useMemo(
    () =>
      canShowOrganizationInsights
        ? [
            {
              icon: Users,
              label: 'Total members',
              value: stats.totalMembers,
              helper:
                stats.totalMembers === 0
                  ? 'No teammates have been added to this workspace yet.'
                  : `${stats.activeMembers} active member${
                      stats.activeMembers === 1 ? '' : 's'
                    } across Smart Apps.`,
              accentClassName: 'text-sky-600 dark:text-sky-300',
              progressClassName: 'from-sky-400 via-cyan-400 to-primary/80',
              progressWidth: `${stats.totalMembers > 0 ? 100 : 18}%`,
            },
            {
              icon: UserCheck,
              label: 'Active members',
              value: stats.activeMembers,
              helper:
                stats.totalMembers === 0
                  ? 'Activity updates will appear when members join the workspace.'
                  : `${activePercentage}% of members are currently active.`,
              accentClassName: 'text-emerald-600 dark:text-emerald-300',
              progressClassName: 'from-emerald-400 via-lime-400 to-primary/80',
              progressWidth: `${clamp(activePercentage, 18, 100)}%`,
            },
            {
              icon: UserX,
              label: 'Inactive members',
              value: stats.inactiveMembers,
              helper:
                stats.inactiveMembers > 0
                  ? `${stats.inactiveMembers} member${
                      stats.inactiveMembers === 1 ? '' : 's'
                    } may need access review.`
                  : 'No inactive members need attention right now.',
              accentClassName: 'text-amber-600 dark:text-amber-300',
              progressClassName: 'from-amber-400 via-orange-400 to-primary/80',
              progressWidth: `${clamp(
                stats.totalMembers
                  ? Math.round((stats.inactiveMembers / stats.totalMembers) * 100)
                  : 0,
                18,
                100,
              )}%`,
            },
            {
              icon: UserPlus,
              label: 'New this month',
              value: stats.newMembersThisMonth,
              helper:
                stats.newMembersThisMonth > 0
                  ? `${stats.newMembersThisMonth} teammate${
                      stats.newMembersThisMonth === 1 ? '' : 's'
                    } joined in the last 30 days.`
                  : 'No new members were added in the last 30 days.',
              accentClassName: 'text-violet-600 dark:text-violet-300',
              progressClassName: 'from-violet-400 via-fuchsia-400 to-primary/80',
              progressWidth: `${clamp(
                stats.totalMembers
                  ? Math.round((stats.newMembersThisMonth / stats.totalMembers) * 100)
                  : 0,
                18,
                100,
              )}%`,
            },
          ]
        : [
            {
              icon: Package,
              label: 'Subscribed products',
              value: subscribedProducts.length,
              helper: `${subscribedProducts.length} active product${
                subscribedProducts.length === 1 ? '' : 's'
              } available in this workspace.`,
              accentClassName: 'text-cyan-600 dark:text-cyan-300',
              progressClassName: 'from-cyan-400 via-sky-400 to-primary/80',
              progressWidth: `${clamp(
                products.length
                  ? Math.round((subscribedProducts.length / products.length) * 100)
                  : 0,
                24,
                100,
              )}%`,
            },
            {
              icon: CalendarDays,
              label: 'Date range',
              value: selectedRangeMetricValue,
              helper: `Dashboard is filtered to ${selectedRangeLabel.toLowerCase()}.`,
              accentClassName: 'text-indigo-600 dark:text-indigo-300',
              progressClassName: 'from-indigo-400 via-violet-400 to-primary/80',
              progressWidth: `${
                selectedRange === '30'
                  ? 100
                  : selectedRange === '14'
                    ? 64
                    : selectedRange === 'custom'
                      ? 82
                      : 38
              }%`,
            },
            {
              icon: Clock3,
              label: 'Refresh cadence',
              value: selectedInterval?.value === 'off' ? 'Manual' : 'Auto',
              helper: refreshCadenceLabel,
              accentClassName: 'text-emerald-600 dark:text-emerald-300',
              progressClassName: 'from-emerald-400 via-cyan-400 to-primary/80',
              progressWidth: `${selectedInterval?.value === 'off' ? 28 : 78}%`,
            },
          ],
    [
      activePercentage,
      canShowOrganizationInsights,
      products.length,
      refreshCadenceLabel,
      selectedInterval?.value,
      selectedRange,
      selectedRangeLabel,
      selectedRangeMetricValue,
      stats.activeMembers,
      stats.inactiveMembers,
      stats.newMembersThisMonth,
      stats.totalMembers,
      subscribedProducts.length,
    ],
  );

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(
      `${DASHBOARD_TILELAYOUT}_DATE_FILTER`,
      JSON.stringify({ range: selectedRange, dates }),
    );
  }, [dates, selectedRange]);

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(
      `${DASHBOARD_TILELAYOUT}_REFRESH_INTERVAL`,
      JSON.stringify(selectedInterval),
    );
  }, [selectedInterval]);

  React.useEffect(() => {
    if (selectedInterval?.value === 'off') {
      return undefined;
    }

    const intervalInMinutes = Number.parseInt(selectedInterval?.value, 10);

    if (!Number.isFinite(intervalInMinutes)) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setRefreshTick((previousValue) => previousValue + 1);
    }, intervalInMinutes * 60 * 1000);

    return () => window.clearInterval(intervalId);
  }, [selectedInterval]);

  React.useEffect(() => {
    let ignore = false;

    const loadDashboardData = async () => {
      if (!tenantID || !user) {
        setProducts([]);
        setMembers([]);
        setStats(DEFAULT_STATS);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      try {
        const accessToken = getTokensFromStorage()?.accessToken;
        const tasks = [listProducts({ tenantId: tenantID, type: 'organization' })];

        if (canShowOrganizationInsights) {
          tasks.push(
            fetchOrganizationMembers({
              accessToken,
              tenantID,
              user,
            }),
          );
        }

        const [productsResult, membersResult] = await Promise.allSettled(tasks);

        if (ignore) {
          return;
        }

        setProducts(
          productsResult.status === 'fulfilled' &&
            Array.isArray(productsResult.value)
            ? productsResult.value
            : [],
        );

        if (canShowOrganizationInsights && membersResult?.status === 'fulfilled') {
          setMembers(membersResult.value.members);
          setStats(membersResult.value.stats);
        } else {
          setMembers([]);
          setStats(DEFAULT_STATS);
        }
      } catch (error) {
        if (!ignore) {
          console.error('Error loading dashboard data:', error);
          setProducts([]);
          setMembers([]);
          setStats(DEFAULT_STATS);
        }
      } finally {
        if (!ignore) {
          setIsLoading(false);
        }
      }
    };

    void loadDashboardData();

    return () => {
      ignore = true;
    };
  }, [
    canShowOrganizationInsights,
    dates.endDate,
    dates.startDate,
    getTokensFromStorage,
    refreshTick,
    selectedRange,
    tenantID,
    user,
  ]);

  const handleRangeChange = (value) => {
    setSelectedRange(value);

    if (value === 'custom') {
      return;
    }

    const today = new Date();
    const nextStartDate = new Date(today);

    if (value === '14') {
      nextStartDate.setDate(today.getDate() - 14);
    } else if (value === '30') {
      nextStartDate.setDate(today.getDate() - 30);
    } else {
      nextStartDate.setDate(today.getDate() - 7);
    }

    setDates({
      startDate: formatDateToInputValue(nextStartDate),
      endDate: formatDateToInputValue(today),
    });
  };

  const updateDateField = (field, value) => {
    setDates((previousValue) => ({
      ...previousValue,
      [field]: value,
    }));
  };

  return (
    <div className='tw dashboard-theme' style={{ fontFamily: DASHBOARD_FONT_FAMILY }}>
      <div className='tw-container flex w-full flex-col gap-5 py-5 text-[13px] text-foreground sm:py-6 [&_a]:cursor-pointer [&_button]:cursor-pointer'>
        <section className='overflow-visible pt-1 text-foreground'>
          <div className='w-full space-y-1'>
            <div className='!mb-0 !text-[28px] font-medium leading-none tracking-tight text-foreground sm:!text-[30px]'>
              Dashboard
            </div>
            <div className='!mt-3 max-w-3xl text-[13px] leading-6 text-muted-foreground'>
              Monitor products, organization activity, and workspace status from
              one place.
            </div>
          </div>

          <div className='mt-6 flex flex-col gap-6 xl:flex-row xl:items-end xl:justify-between'>
            <div className='min-w-0 space-y-4'>
              <div className='flex flex-wrap gap-2'>
                <HeroMetaPill icon={CalendarDays} label={selectedRangeLabel} />
                <HeroMetaPill icon={Clock3} label={refreshCadenceLabel} />
                <HeroMetaPill
                  icon={Package}
                  label={`${subscribedProducts.length} subscribed product${
                    subscribedProducts.length === 1 ? '' : 's'
                  }`}
                />
              </div>
            </div>

            <div className='flex w-full flex-col gap-3 xl:ml-auto xl:w-auto xl:items-end'>
              {selectedRange === 'custom' ? (
                <div className='grid w-full gap-3 sm:grid-cols-2 sm:justify-end xl:w-auto'>
                  <DashboardDateField
                    label='Start Date'
                    value={dates.startDate}
                    onChange={(value) => updateDateField('startDate', value)}
                  />
                  <DashboardDateField
                    label='End Date'
                    value={dates.endDate}
                    onChange={(value) => updateDateField('endDate', value)}
                  />
                </div>
              ) : null}

              <div className='grid w-full grid-cols-[minmax(0,1fr)_40px_40px] gap-2 sm:ml-auto sm:w-fit sm:grid-cols-[220px_40px_40px] xl:items-end'>
                <label className='flex min-w-0 flex-col gap-2 xl:min-w-[220px]'>
                  {selectedRange === 'custom' ? (
                    <span className='text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground'>
                      Range
                    </span>
                  ) : (
                    <span className='h-[18px]' />
                  )}
                  <Select value={selectedRange} onValueChange={handleRangeChange}>
                    <SelectTrigger className='h-10 rounded-xl border-border/60 bg-background/80 px-4 text-[14px] text-foreground shadow-none [&>svg]:text-muted-foreground'>
                      <div className='flex min-w-0 items-center gap-3'>
                        <CalendarDays className='h-4 w-4 shrink-0 text-muted-foreground' />
                        <SelectValue placeholder='Select range'>
                          {selectedRangeControlLabel}
                        </SelectValue>
                      </div>
                    </SelectTrigger>
                    <SelectContent>
                      {DATE_RANGE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.value === 'custom' ? 'Custom Range' : option.text}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </label>

                <label className='flex min-w-[40px] self-end flex-col gap-2'>
                  <span className='h-[18px]' aria-hidden='true' />
                  <Select
                    value={selectedInterval?.value ?? 'off'}
                    onValueChange={(value) => {
                      const nextInterval =
                        REFRESH_INTERVAL.find((option) => option.value === value) ||
                        selectedInterval;

                      setSelectedInterval(nextInterval);
                    }}
                  >
                    <SelectTrigger
                      aria-label={`Refresh cadence: ${selectedInterval?.text || selectedInterval?.label || 'Off'}`}
                      className='h-10 w-10 justify-center rounded-xl border-border/60 bg-background/80 px-0 text-foreground shadow-none [&>svg:last-child]:hidden [&>svg]:text-muted-foreground'
                    >
                      <div className='flex min-w-0 items-center justify-center'>
                        <Clock3 className='h-4 w-4 shrink-0 text-muted-foreground' />
                        <span className='sr-only'>
                          {selectedInterval?.text || selectedInterval?.label || 'Off'}
                        </span>
                      </div>
                    </SelectTrigger>
                    <SelectContent>
                      {REFRESH_INTERVAL.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.text || option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </label>

                <Button
                  type='button'
                  variant='outline'
                  size='icon'
                  onClick={() => setRefreshTick((previousValue) => previousValue + 1)}
                  className='h-10 w-10 self-end rounded-xl border-border/60 bg-background/80 text-foreground shadow-none hover:bg-accent hover:text-foreground'
                  aria-label='Refresh dashboard'
                >
                  <RefreshCcw className={cn('h-4 w-4', isLoading && 'animate-spin')} />
                </Button>
              </div>
            </div>
          </div>
        </section>

        <section>
          <DashboardSurface className='overflow-hidden rounded-lg border-border/40 bg-card/95'>
            <CardContent className='relative p-5 sm:p-6 xl:p-7'>
              <div
                className={cn(
                  'absolute right-5 top-5 inline-flex shrink-0 items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-medium sm:right-6 sm:top-6 xl:right-7 xl:top-7',
                  isLoading
                    ? 'border-primary/20 bg-primary/10 text-primary'
                    : 'border-emerald-500/20 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
                )}
              >
                <span
                  className={cn(
                    'h-2.5 w-2.5 rounded-full',
                    isLoading ? 'bg-primary' : 'bg-emerald-500',
                  )}
                />
                {isLoading ? 'Refreshing' : 'Live'}
              </div>

              <div className='flex flex-col gap-6 xl:flex-row xl:items-end xl:justify-between'>
                <div className='min-w-0 space-y-4 pr-14 sm:pr-20'>
                  <div className='text-[10px] font-semibold uppercase tracking-[0.24em] text-muted-foreground/90'>
                    Workspace overview
                  </div>

                  <div className='space-y-2.5'>
                    <div className='max-w-3xl text-[24px] font-semibold leading-[1.08] tracking-[-0.045em] text-foreground sm:text-[30px]'>
                      <span className='font-medium text-foreground/72'>
                        {getGreeting()},
                      </span>{' '}
                      <span className='bg-gradient-to-r from-foreground via-foreground to-foreground/70 bg-clip-text text-transparent'>
                        {displayName}
                      </span>
                    </div>
                    <p className='max-w-xl text-[14px] leading-6 text-muted-foreground'>
                      Welcome to your Smart Apps dashboard.
                    </p>
                  </div>
                </div>

                <div className='flex w-full flex-col gap-4 xl:w-auto xl:items-end'>
                  <div className='flex w-full flex-col gap-3 sm:flex-row xl:w-auto'>
                    <Button
                      type='button'
                      onClick={() => navigate('/products', { state: { isBack: true } })}
                      className='h-10 w-full rounded-lg px-5 text-[14px] font-medium text-white shadow-none sm:w-auto'
                    >
                      View products
                    </Button>
                    <Button
                      type='button'
                      variant='outline'
                      onClick={() => navigate('/profile', { state: { isBack: true } })}
                      className='h-10 w-full rounded-lg border-border/60 bg-transparent px-5 text-[14px] font-medium shadow-none hover:bg-accent/40 sm:w-auto'
                    >
                      Manage app keys
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </DashboardSurface>
        </section>

        <section>
          <div
            className={cn(
              'grid grid-cols-1 gap-3 md:grid-cols-2',
              canShowOrganizationInsights ? 'xl:grid-cols-4' : 'xl:grid-cols-3',
            )}
          >
            {isLoading
              ? workspaceOverviewCards.map((item) => (
                  <WorkspaceMetricCardSkeleton key={item.label} />
                ))
              : workspaceOverviewCards.map((item) => (
                  <WorkspaceMetricCard key={item.label} {...item} />
                ))}
          </div>

        </section>

        <section
          className={cn(
            'grid gap-4',
            canShowOrganizationInsights
              ? 'xl:grid-cols-[minmax(0,1.05fr)_minmax(340px,0.95fr)]'
              : 'grid-cols-1',
          )}
        >
          <DashboardSurface className='overflow-hidden'>
            <PanelHeader
              title='Subscribed products'
              description='Products and active features available in this workspace.'
              action={
                <button
                  type='button'
                  onClick={() => navigate('/products', { state: { isBack: true } })}
                  className='inline-flex h-8 items-center rounded-lg border border-border/60 bg-background/70 px-3 text-[12px] font-medium text-foreground transition-colors hover:bg-accent/50'
                >
                  View all
                </button>
              }
            />
            <CardContent className='p-0'>
            {visibleProducts.length > 0 ? (
              <div>
                <div className='px-5 py-2 text-[13px] text-muted-foreground'>
                  Showing {visibleProducts.length} product
                  {visibleProducts.length === 1 ? '' : 's'} in this workspace
                </div>
                <div className='divide-y divide-border/25'>
                  {visibleProducts.map((product, index) => {
                    const productKey = getProductKey(product, index);
                    const visual = getProductVisual(product, index);
                    const productStatus = getProductStatus(product);
                    const subscribedFeatureRows = getSubscribedFeatureRows(product);
                    const visibleFeatureRows = subscribedFeatureRows.slice(0, 2);
                    const hiddenFeatureCount = Math.max(
                      subscribedFeatureRows.length - visibleFeatureRows.length,
                      0,
                    );
                    const isExpanded = expandedProductId === productKey;

                    return (
                      <div
                        key={productKey}
                        className='px-5 py-3 transition-colors hover:bg-primary/3'
                      >
                        <button
                          type='button'
                          onClick={() =>
                            setExpandedProductId((currentValue) =>
                              currentValue === productKey ? null : productKey,
                            )
                          }
                          className='group flex w-full items-start gap-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 '
                          aria-expanded={isExpanded ? 'true' : 'false'}
                        >
                          <div
                            className={cn(
                              'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg',
                              visual.tone.bg,
                              visual.tone.text,
                            )}
                          >
                            <visual.Icon className='h-4 w-4' />
                          </div>

                          <div className='min-w-0 flex-1 space-y-1'>
                            <div className='flex flex-wrap items-center gap-1.5'>
                              <div className='truncate text-[14px] font-semibold text-foreground transition-colors'>
                                {product?.display_name || 'Untitled product'}
                              </div>
                              <StatusBadge status={productStatus} />
                            </div>
                            <div className='flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-[13px] text-muted-foreground'>
                              <span className='truncate'>{visual.subtitle}</span>
                              <span>
                                {subscribedFeatureRows.length} subscribed feature
                                {subscribedFeatureRows.length === 1 ? '' : 's'}
                              </span>
                            </div>
                          </div>

                          <div className='flex shrink-0 items-center gap-2.5'>
                            <div className='hidden min-w-[56px] text-right sm:block'>
                              <div className='text-[28px] font-semibold leading-none tracking-[-0.03em] text-foreground'>
                                {subscribedFeatureRows.length}
                              </div>
                              <div className='mt-1 text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground'>
                                Features
                              </div>
                            </div>
                            <div className='flex h-9 w-9 items-center justify-center rounded-lg border border-border/50 bg-background/70 text-muted-foreground transition-colors group-hover:bg-accent/50 group-hover:text-foreground'>
                              <ChevronDown
                                className={cn(
                                  'h-4 w-4 transition-transform',
                                  isExpanded && 'rotate-180',
                                )}
                              />
                            </div>
                          </div>
                        </button>

                        {isExpanded && subscribedFeatureRows.length > 0 ? (
                          <div className='mt-3 rounded-lg bg-muted/20'>
                            {visibleFeatureRows.map((feature, featureIndex) => (
                              <div
                                key={feature.id}
                                className={cn(
                                  'flex flex-col gap-1.5 px-4 py-2.5 sm:flex-row sm:items-center sm:justify-between',
                                  featureIndex > 0 && 'border-t border-border/20',
                                )}
                              >
                                <div className='min-w-0'>
                                  <div className='flex flex-wrap items-center gap-1.5'>
                                    <div className='truncate text-[12px] font-medium text-foreground'>
                                      {feature.name}
                                    </div>
                                    <StatusBadge status={feature.status} />
                                  </div>
                                  <div className='mt-1 text-[11px] text-muted-foreground'>
                                    Expiration date:{' '}
                                    {feature.expirationDate
                                      ? formatDateStandard(feature.expirationDate)
                                      : '--'}
                                  </div>
                                </div>
                              </div>
                            ))}
                            {hiddenFeatureCount > 0 ? (
                              <div className='border-t border-border/20 px-4 py-2.5 text-[11px] text-muted-foreground'>
                                +{hiddenFeatureCount} more subscribed feature
                                {hiddenFeatureCount === 1 ? '' : 's'}
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className='px-5 py-10 text-center text-[13px] text-muted-foreground'>
                No subscribed products found for the selected tenant.
              </div>
            )}
          </CardContent>
        </DashboardSurface>

        {canShowOrganizationInsights ? (
          <div className='flex flex-col gap-4'>
            <DashboardSurface>
              <PanelHeader
                title='Organization users'
                description='Member activity and status across your organization.'
              />
              <CardContent className='px-5 pb-5 pt-0'>
                <DonutSummary
                  percentage={activePercentage}
                  activeMembers={stats.activeMembers}
                  inactiveMembers={stats.inactiveMembers}
                />
              </CardContent>
            </DashboardSurface>

            <DashboardSurface>
              <PanelHeader
                title='Recent members'
                description='Newest people added to your workspace.'
                action={
                  <button
                    type='button'
                    onClick={() =>
                      navigate('/organization-users', { state: { isBack: true } })
                    }
                    className='text-[13px] font-medium text-foreground transition-colors hover:text-primary'
                  >
                    See all
                  </button>
                }
              />
              <CardContent className='px-5 pb-5 pt-0'>
                {recentMembers.length > 0 ? (
                  <div className='space-y-1'>
                    {recentMembers.map((member, index) => {
                      const avatarTone = AVATAR_TONES[index % AVATAR_TONES.length];
                      const memberName =
                        `${member?.FirstName ?? ''} ${member?.LastName ?? ''}`.trim() ||
                        extractName(member?.EmailAddress);

                      return (
                        <div
                          key={member?.UserId || member?.EmailAddress || index}
                          className='flex items-center gap-3 rounded-lg border-b border-border/55 py-3 last:border-b-0 last:pb-0'
                        >
                          <div
                            className={cn(
                              'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold',
                              avatarTone,
                            )}
                          >
                            {getInitials(
                              member?.FirstName,
                              member?.LastName,
                              member?.EmailAddress,
                            )}
                          </div>

                          <div className='min-w-0 flex-1'>
                            <div className='truncate text-[13px] font-semibold text-foreground'>
                              {memberName}
                            </div>
                            <div className='truncate text-[12px] text-muted-foreground'>
                              {member?.EmailAddress || 'No email'}
                            </div>
                          </div>

                          <div className='hidden text-[12px] text-muted-foreground sm:block'>
                            {formatShortDate(member?.CreatedDate)}
                          </div>
                          <span
                            className={cn(
                              'h-2.5 w-2.5 shrink-0 rounded-full',
                              member?.Active ? 'bg-lime-600' : 'bg-border',
                            )}
                            aria-hidden='true'
                          />
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className='rounded-lg border border-dashed border-border/70 px-4 py-8 text-center text-[13px] text-muted-foreground'>
                    No organization members found.
                  </div>
                )}
              </CardContent>
            </DashboardSurface>
          </div>
        ) : null}
      </section>


      {isLoading ? (
        <div className='pointer-events-none fixed inset-x-0 bottom-6 z-10 flex justify-center'>
          <div className='rounded-full border border-border/70 bg-card px-4 py-2 shadow-lg'>
            <div className='flex items-center gap-2 text-[13px] text-muted-foreground'>
              <RefreshCcw className='h-4 w-4 animate-spin' />
              <span>Refreshing dashboard</span>
            </div>
          </div>
        </div>
      ) : null}
      </div>
    </div>
  );
};

export default Home;
